/**
 * Lời gọi API cho nhóm màn hình biên tập và duyệt bản tin (MH-11 đến MH-16),
 * đúng hình dạng của `apps/api/src/ban-tin/*.controller.ts`.
 */
import { chuoiTruyVan, goi, LoiApi } from './goi-chung';
import { layToken, type PhongBiNoiBo } from './khach-api';
import type { TrangDuLieu } from './thiet-bi-api';

export type TrangThaiBanTin = 'tao_moi' | 'chua_duyet' | 'da_duyet' | 'dang_phat' | 'da_phat' | 'da_huy';

export interface DongBanTin {
  id: string;
  banTinId: string;
  tieuDe: string;
  loaiBanTin: string;
  mucDoUuTien: string;
  loaiLinhVuc: string;
  noiDungTomTat: string;
  noiDung: string | null;
  thoiLuong: string | null;
  thoiGianSanXuat: number;
  tacGia: unknown;
  diaBanTao: string;
  tenDiaBanTao: string | null;
  nguonTin: string;
  tiepAm: number | null;
  nguonTiepAm: string | null;
  thongTinChiTiet: unknown;
  trangThai: TrangThaiBanTin;
  nguonTrungUongId: string | null;
  nguoiTaoId: string | null;
  /** Tên đăng nhập người tạo (máy chủ bổ sung 30/09/2026; bản cũ không có). */
  tenNguoiTao?: string | null;
  /** Địa bàn chia sẻ nội dung: mã tỉnh (cấp sở) và mã các xã. */
  diaBanChiaSe?: string[];
  nguoiDuyetId: string | null;
  /**
   * Vùng phát bảng tin (hợp đồng gộp kho 29/10/2026 mục 1): bản tin hình ảnh/video `'0'`–`'3'`,
   * bản tin âm thanh `null` (gửi bảng tin coi là `'0'`). Máy chủ cũ không trả.
   */
  vungPhat?: string | null;
  /** Tệp của bản tin hình ảnh/video; `null` khi chưa tải hoặc là bản tin âm thanh. */
  tep?: TepBanTin | null;
  createdAt: string;
  updatedAt: string;
}

/** Tệp đính kèm của bản tin hình ảnh/video (`ban_tin.tep_*`). */
export interface TepBanTin {
  ten: string;
  mime: string;
  kichThuoc: number;
}

export interface BoLocBanTin {
  page?: number;
  pageSize?: number;
  keyword?: string;
  nguonId?: string;
  trangThai?: string;
  loaiLinhVuc?: string;
  mucDoUuTien?: string;
  nguoiTaoId?: string;
  /** Loại bản tin, nhiều mã cách nhau dấu phẩy (vd `1,3` cho cụm loa). */
  loaiBanTin?: string;
  /** `1` = Kho thông tin nguồn: bản tin đã duyệt được chia sẻ tới địa bàn người dùng. */
  chiaSe?: string;
  tuThoiDiem?: string;
  denThoiDiem?: string;
}

export type LoaiNguonDoan = 'tai_len' | 'kho' | 'tts';

export interface DongDoanAmThanh {
  id: string;
  thuTu: number;
  loaiNguon: LoaiNguonDoan;
  audioAssetId: string | null;
  ttsJobId: string | null;
  thoiLuongGiay: number | null;
  /** URL nghe thử; máy chủ chưa trả trong 1.0.2, giao diện tự dựng khi có urlTaiVe kho. */
  urlNgheThu?: string | null;
  ten?: string | null;
}

export interface DongDuyetLog {
  id: string;
  trangThaiTruoc: string | null;
  trangThaiSau: string;
  nguoiThucHienId: string | null;
  yKien: string | null;
  thoiDiem: string;
}

/**
 * Thống kê số bản tin đã xuất bản theo loại (Công văn 276: năm loại bản tin).
 * Khoá `theoLoai` là mã loại bản tin: '1' văn bản, '2' hình ảnh, '3' âm thanh,
 * '4' video, '5' trực tiếp.
 */
export interface ThongKeBanTin {
  tong: number;
  theoLoai: { '1': number; '2': number; '3': number; '4': number; '5': number };
}

/* ------------------------- Quy ước loại bản tin (gộp kho) ------------------------- */

/** Bản tin dùng được cho cụm loa: văn bản chuyển giọng nói và âm thanh. */
export const LOAI_BAN_TIN_CUM_LOA = '1,3';
/** Bản tin dùng được cho bảng tin điện tử: mọi loại trừ trực tiếp. */
export const LOAI_BAN_TIN_BANG_TIN = '1,2,3,4';
/** Trạng thái bản tin được chọn vào lịch / phát (cả hai loại thiết bị). */
export const TRANG_THAI_CHON_PHAT = 'da_duyet,dang_phat,da_phat';

/** Ô "Loại bản tin" trên CMS: Âm thanh (mã 1 hoặc 3), Hình ảnh (2), Video (4). */
export type NhomLoaiBanTin = 'am-thanh' | 'hinh-anh' | 'video';
export const DS_NHOM_LOAI_BAN_TIN: readonly NhomLoaiBanTin[] = ['am-thanh', 'hinh-anh', 'video'];

/** Mã loại bản tin → nhóm hiển thị; mã lạ (5 trực tiếp) trả null. */
export function nhomLoaiBanTin(loai: string | null | undefined): NhomLoaiBanTin | null {
  if (loai === '1' || loai === '3') return 'am-thanh';
  if (loai === '2') return 'hinh-anh';
  if (loai === '4') return 'video';
  return null;
}

/** Nhóm → giá trị lọc `loaiBanTin` gửi máy chủ. */
export const LOC_THEO_NHOM_LOAI: Record<NhomLoaiBanTin, string> = {
  'am-thanh': '1,3',
  'hinh-anh': '2',
  video: '4',
};

/** VungPhat CV276: 0 toàn màn hình, 1 vùng thông báo, 2 vùng bên trái, 3 vùng bên phải. */
export const DS_VUNG_PHAT = ['0', '1', '2', '3'] as const;
export type VungPhat = (typeof DS_VUNG_PHAT)[number];

/** Định dạng tệp nhận cho bản tin hình ảnh / video (khớp kiểm của máy chủ). */
export const DINH_DANG_TEP: Record<'2' | '4', { mime: string[]; duoi: string[] }> = {
  '2': { mime: ['image/jpeg', 'image/png', 'image/webp'], duoi: ['jpg', 'jpeg', 'png', 'webp'] },
  '4': { mime: ['video/mp4'], duoi: ['mp4'] },
};

/** Tệp có đúng loại bản tin hình ảnh / video không (theo MIME; không có MIME thì xét đuôi). */
export function tepDungLoaiBanTin(tep: { name: string; type: string }, loai: '2' | '4'): boolean {
  const dd = DINH_DANG_TEP[loai];
  if (tep.type) return dd.mime.includes(tep.type);
  const duoi = tep.name.split('.').pop()?.toLowerCase() ?? '';
  return dd.duoi.includes(duoi);
}

/** Thời lượng `HH:mm:ss` (hoặc `mm:ss`) → số giây; rỗng hoặc sai khuôn trả null. */
export function giayTuThoiLuong(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = /^(?:(\d{1,3}):)?([0-5]?\d):([0-5]\d)$/.exec(s.trim());
  if (!m) return null;
  return Number(m[1] ?? 0) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}

function gocApi(): string {
  return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
}

/** Tiến trình gửi tệp lên: số byte đã gửi / tổng số byte của thân yêu cầu. */
export interface TienTrinhTaiLen {
  daGui: number;
  tong: number;
}

/**
 * `POST /bulletins/:id/file` — multipart trường `file`, tuỳ chọn `thoiLuong` `HH:mm:ss` (video).
 * Chỉ cho bản tin hình ảnh (`2`) / video (`4`) còn sửa được. Dùng `XMLHttpRequest` thay `fetch` vì
 * `fetch` không báo tiến trình gửi lên — video tới 500 MB cần thanh %.
 */
function taiTepBanTin(
  id: string,
  tep: File,
  thoiLuong?: string,
  onTienTrinh?: (tt: TienTrinhTaiLen) => void,
): Promise<{ id: string; tep: TepBanTin }> {
  const fd = new FormData();
  fd.append('file', tep);
  if (thoiLuong) fd.append('thoiLuong', thoiLuong);
  const token = layToken();
  return new Promise((xong, loi) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${gocApi()}/api/v1/bulletins/${encodeURIComponent(id)}/file`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (onTienTrinh && e.lengthComputable) onTienTrinh({ daGui: e.loaded, tong: e.total });
    };
    xhr.onerror = () => loi(new LoiApi('NETWORK_ERROR', 'Mất kết nối khi tải tệp lên'));
    xhr.ontimeout = () => loi(new LoiApi('NETWORK_ERROR', 'Hết thời gian chờ khi tải tệp lên'));
    xhr.onload = () => {
      let pb: PhongBiNoiBo<{ id: string; tep: TepBanTin }>;
      try {
        pb = JSON.parse(xhr.responseText) as PhongBiNoiBo<{ id: string; tep: TepBanTin }>;
      } catch {
        loi(new LoiApi('INTERNAL_ERROR', `Máy chủ trả lỗi ${xhr.status}`));
        return;
      }
      if (!pb.success) loi(new LoiApi(pb.code, pb.message, pb.requestId));
      else xong(pb.data as { id: string; tep: TepBanTin });
    };
    xhr.send(fd);
  });
}

export const banTinApi = {
  /** `GET /bulletins/statistics` — `nguonId` rỗng nghĩa là cả tỉnh. */
  thongKe: (tham: { nguonId?: string }, signal?: AbortSignal): Promise<ThongKeBanTin> =>
    goi(`/api/v1/bulletins/statistics${chuoiTruyVan(tham)}`, { signal }),

  danhSach: (tham: BoLocBanTin, signal?: AbortSignal): Promise<TrangDuLieu<DongBanTin>> =>
    goi(`/api/v1/bulletins${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`, { signal }),

  hangDoiChoDuyet: (
    tham: { page?: number; pageSize?: number; nguonId?: string; loaiBanTin?: string },
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongBanTin & { daNghe: boolean }>> =>
    goi(
      `/api/v1/bulletins/pending-approval${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`,
      {
        signal,
      },
    ),

  chiTiet: (id: string, signal?: AbortSignal): Promise<DongBanTin> =>
    goi(`/api/v1/bulletins/${id}`, { signal }),

  tao: (than: Record<string, unknown>): Promise<{ id: string; banTinId: string }> =>
    goi('/api/v1/bulletins', { phuongThuc: 'POST', than }),

  sua: (id: string, than: Record<string, unknown>): Promise<{ id: string }> =>
    goi(`/api/v1/bulletins/${id}`, { phuongThuc: 'PUT', than }),

  xoa: (id: string): Promise<{ id: string }> => goi(`/api/v1/bulletins/${id}`, { phuongThuc: 'DELETE' }),

  /** Tải tệp cho bản tin hình ảnh / video. */
  taiTep: taiTepBanTin,

  /** `GET /bulletins/:id/file-url` — link ký ngắn hạn để xem trước tệp. */
  linkTep: (id: string, signal?: AbortSignal): Promise<{ url: string; hetHan?: number }> =>
    goi(`/api/v1/bulletins/${encodeURIComponent(id)}/file-url`, { signal }),

  nhanBan: (id: string): Promise<{ id: string; banTinId: string }> =>
    goi(`/api/v1/bulletins/${id}/duplicate`, { phuongThuc: 'POST' }),

  trinhDuyet: (id: string): Promise<{ id: string; trangThai: string }> =>
    goi(`/api/v1/bulletins/${id}/submit`, { phuongThuc: 'POST' }),

  duyet: (id: string, quyetDinh: string, yKien?: string): Promise<{ id: string; trangThai: string }> =>
    goi(`/api/v1/bulletins/${id}/approve`, { phuongThuc: 'POST', than: { quyetDinh, yKien } }),

  huy: (id: string, lyDo?: string): Promise<{ id: string; trangThai: string }> =>
    goi(`/api/v1/bulletins/${id}/cancel`, { phuongThuc: 'POST', than: { lyDo } }),

  danhDauDaNghe: (id: string): Promise<{ id: string; daNghe: true }> =>
    goi(`/api/v1/bulletins/${id}/listened`, { phuongThuc: 'POST' }),

  nhatKyDuyet: (id: string, signal?: AbortSignal): Promise<DongDuyetLog[]> =>
    goi(`/api/v1/bulletins/${id}/approval-log`, { signal }),

  doanAmThanh: {
    danhSach: (banTinId: string, signal?: AbortSignal): Promise<DongDoanAmThanh[]> =>
      goi(`/api/v1/bulletins/${banTinId}/segments`, { signal }),

    them: (
      banTinId: string,
      than: { loaiNguon: LoaiNguonDoan; audioAssetId?: string; ttsJobId?: string },
    ): Promise<DongDoanAmThanh> =>
      goi(`/api/v1/bulletins/${banTinId}/segments`, { phuongThuc: 'POST', than }),

    xoa: (banTinId: string, segId: string): Promise<{ id: string }> =>
      goi(`/api/v1/bulletins/${banTinId}/segments/${segId}`, { phuongThuc: 'DELETE' }),

    doiThuTu: (banTinId: string, thuTu: string[]): Promise<{ id: string }> =>
      goi(`/api/v1/bulletins/${banTinId}/segments/order`, { phuongThuc: 'PUT', than: { thuTu } }),
  },

  ghepAmThanh: (danhSachAsset: string[]): Promise<{ id: string }> =>
    goi('/api/v1/bulletins/merge', { phuongThuc: 'POST', than: { danhSachAsset } }),

  /** Danh mục giọng đọc (bổ sung ngoài OpenAPI). */
  giongDoc: (
    signal?: AbortSignal,
  ): Promise<{
    nhaCungCap: string;
    macDinh: string;
    items: { ma: string; ten: string; moTa: string; vung: string }[];
  }> => goi('/api/v1/tts/voices', { signal }),

  taoTts: (than: { vanBan: string; giongDoc?: string; tocDo?: number }): Promise<{ id: string }> =>
    goi('/api/v1/tts/jobs', { phuongThuc: 'POST', than }),

  // Hình dạng thật của `CongViecAmThanhService.chiTiet`; `urlNgheThu` là link nghe qua
  // gateway (có khi việc đã xong), `duongDanKetQua` là khoá lưu trữ nội bộ.
  chiTietTts: (
    id: string,
    signal?: AbortSignal,
  ): Promise<{
    id: string;
    trangThai: string;
    duongDanKetQua: string | null;
    thoiLuongGiay: number | null;
    maLoi: string | null;
    urlNgheThu?: string | null;
  }> => goi(`/api/v1/tts/jobs/${id}`, { signal }),
};
